import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import { CollaborativeUser } from 'app/components/Song/Lyrics/TextEditor/types';
import { ReactComponent as Download } from 'resources/images/download.svg';
import { ReactComponent as CornerUpLeft } from 'resources/images/corner-up-left.svg';
import { ReactComponent as CornerUpRight } from 'resources/images/corner-up-right.svg';
import { getAvatarImage } from 'app/utils/utils';
import { LiveUsersDialog } from 'app/components/Song/Lyrics/LiveUsersDialog';
import { AvatarBadge, AvatarGroup, Flex } from '@chakra-ui/react';
import { ChakraAvatar } from 'app/components/Core';
import { MenuBarIcon } from 'app/components/Song/Lyrics/MenuBarIcon';
import { TextFormattingMenu } from 'app/components/Song/Lyrics/TextFormattingMenu';
import { Lyrics2Configuration } from 'types/lyrics.types';
import { CoWriterSelect } from 'app/components/CoWriterBar';
import { getStyles } from './styles';
import { useIsSticky } from './useIsSticky';

interface MenuBarProps {
  editor: Editor;
  users: Array<CollaborativeUser & { isSelf?: boolean }>;
  onDownloadClick: () => void;
  isDisabledDownloadButton: boolean;
  disabled: boolean;
  config: Lyrics2Configuration;
}

export const MenuBar: React.FC<MenuBarProps> = ({
  editor,
  users,
  onDownloadClick,
  isDisabledDownloadButton,
  disabled,
  config,
}) => {
  const [isTextFormatOpen, setIsTextFormatOpen] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const handleOpen = () => setOpenUsersDialog(true);
  const handleClose = () => setOpenUsersDialog(false);

  const { ref, isSticky } = useIsSticky();
  const { containerSx, avatarSx, avatarBadgeSx, getAvatarGroupSx } = getStyles(
    isSticky
  );

  if (!editor) {
    return null;
  }

  return (
    <>
      <LiveUsersDialog
        open={openUsersDialog}
        onClose={handleClose}
        users={users}
      />
      <Flex sx={containerSx}>
        <Flex ref={ref} flex={1} maxW={640} justify="space-between" mx={2}>
          <Flex display="inline-flex" alignItems="center" gap={8}>
            <Flex gap={2}>
              <MenuBarIcon
                disabled={disabled}
                icon={CornerUpLeft}
                tooltip="Undo"
                data-testid="undo"
                onClick={() => (editor as any).chain().focus().undo().run()}
              />
              <MenuBarIcon
                disabled={disabled}
                icon={CornerUpRight}
                tooltip="Redo"
                data-testid="redo"
                onClick={() => (editor as any).chain().focus().redo().run()}
              />
            </Flex>
            <TextFormattingMenu
              editor={editor}
              config={config}
              disabled={disabled}
              isOpen={isTextFormatOpen}
              onToggle={setIsTextFormatOpen}
            />
            <MenuBarIcon
              disabled={isDisabledDownloadButton || disabled}
              icon={Download}
              tooltip="Download"
              data-testid="download"
              onClick={onDownloadClick}
            />
            <CoWriterSelect
              tooltip="Co-Writer"
              data-testid="ai-feature"
              editor={editor}
            />
          </Flex>
          {users.length ? (
            <AvatarGroup
              data-testid="open-live-editors"
              max={3}
              onClick={handleOpen}
              cursor="pointer"
              sx={getAvatarGroupSx(users.length + 1)}
            >
              {users.map((user, i) => (
                <ChakraAvatar
                  key={user.clientId}
                  size="sm"
                  initials={user.profile?.initials}
                  src={getAvatarImage(user.profile?.profileImages)}
                  sx={{
                    zIndex: i + 1,
                    colors: user.profile?.avatarColors,
                    ...avatarSx,
                  }}
                >
                  {!user.isSelf && (
                    <AvatarBadge
                      sx={avatarBadgeSx}
                      bg={user.profile?.avatarColors?.text}
                    />
                  )}
                </ChakraAvatar>
              ))}
            </AvatarGroup>
          ) : null}
        </Flex>
      </Flex>
    </>
  );
};
